import { useGameEvents, formatEventTime, separateEventsByTeam, groupEventsByPeriod } from "@/hooks/useGameEvents";
import { useInsightEvents, InsightEvent } from "@/hooks/useInsightEvents";
import { InsightAlertRow, InsightConfirmRow, InsightVarDisallowedRow } from "./InsightEventRow";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { CircleX, Square, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from "@/lib/utils";

interface GameEventsProps {
  fixtureId: number;
  enableRealtime?: boolean;
  gameStatus?: string;
  enablePolling?: boolean;
  homeTeamId: string | null;
  awayTeamId: string | null;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  homeScore?: number;
  awayScore?: number;
  /** When true, removes max-height limit (for use inside scrollable containers like Drawer) */
  inScrollableContainer?: boolean;
}

interface EventIconProps {
  eventType: string;
  eventDetail: string | null;
}

const EventIcon = ({ eventType, eventDetail }: EventIconProps) => {
  // Penalty falhado
  if (eventDetail?.includes('Missed Penalty') || eventDetail?.includes('Penalty Missed')) {
    return <CircleX className="w-5 h-5 text-red-500" />;
  }
  
  switch (eventType) {
    case 'Goal':
      // Auto-golo (laranja para diferenciar)
      if (eventDetail?.includes('Own Goal')) {
        return <span className="text-2xl leading-none text-orange-500">⚽</span>;
      }
      // Golo normal ou penalty (bola verde)
      return <span className="text-2xl leading-none">⚽</span>;
      
    case 'Card':
      if (eventDetail?.includes('Red')) {
        return <Square className="w-4 h-4 text-red-600 fill-red-600" />;
      }
      // Cartão amarelo
      return <Square className="w-4 h-4 text-yellow-500 fill-yellow-500" />;
      
    case 'subst':
      return null;
      
    case 'Var':
      return (
        <Badge 
          variant="outline" 
          className="text-[10px] font-bold px-1.5 py-0 h-5 border-2 border-foreground"
        >
          VAR
        </Badge>
      );
      
    default:
      return <span className="text-muted-foreground">•</span>;
  }
};

export function GameEvents({
  fixtureId, 
  enableRealtime = false, 
  gameStatus, 
  enablePolling = false,
  homeTeamId,
  awayTeamId,
  homeTeamLogo,
  awayTeamLogo,
  homeScore,
  awayScore,
  inScrollableContainer = false
}: GameEventsProps) {
  const { data: events, isLoading, dataUpdatedAt } = useGameEvents(fixtureId, enableRealtime, gameStatus, enablePolling);
  const { data: insightEvents = [] } = useInsightEvents(fixtureId);
  const isLive = gameStatus === 'live';
  
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="animate-pulse space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-muted rounded" />
          ))}
        </div>
      </div>
    );
  }

  const hasGameEvents = events && events.length > 0;
  const hasInsights = insightEvents.length > 0;

  if (!hasGameEvents && !hasInsights) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground text-center py-4">
          No events recorded
        </p>
      </div>
    );
  }

  // Filter out invalid events (coaches can only receive cards, not score goals)
  const validEvents = (events ?? []).filter(event => {
    // If player_id is -1 (coach), only allow Card events with a real name
    if (event.player_id === -1) {
      return event.event_type === 'Card' && !!event.player_name;
    }
    return true;
  });

  const { home: homeEvents, away: awayEvents } = separateEventsByTeam(validEvents, homeTeamId, awayTeamId);
  const periods = groupEventsByPeriod(validEvents);

  const PeriodSeparator = ({ label, score, isFinal }: { label: string; score?: string; isFinal?: boolean }) => (
    <div className={`flex items-center justify-center gap-3 py-3 my-2 ${isFinal ? 'bg-accent/50 rounded-lg' : ''}`}>
      <div className="flex-1 h-px bg-border" />
      <div className="flex items-center gap-2">
        {homeTeamLogo && (
          <Avatar className={isFinal ? "h-6 w-6" : "h-5 w-5"}>
            <AvatarImage src={homeTeamLogo} alt="Home" />
            <AvatarFallback>H</AvatarFallback>
          </Avatar>
        )}
        <span className={`${isFinal ? 'text-sm' : 'text-xs'} font-bold text-muted-foreground uppercase tracking-wider`}>
          {label} {score && `• ${score}`}
        </span>
        {awayTeamLogo && (
          <Avatar className={isFinal ? "h-6 w-6" : "h-5 w-5"}>
            <AvatarImage src={awayTeamLogo} alt="Away" />
            <AvatarFallback>A</AvatarFallback>
          </Avatar>
        )}
      </div>
      <div className="flex-1 h-px bg-border" />
    </div>
  );

  // Helper to determine if event detail should be shown
  const shouldShowEventDetail = (event: typeof events[0]): boolean => {
    // Never show for cards
    if (event.event_type === 'Card') return false;
    
    // Never show for missed penalties
    const isMissedPenalty = event.event_detail?.includes('Missed Penalty') || 
                            event.event_detail?.includes('Penalty Missed');
    if (isMissedPenalty) return false;
    
    // ❌ NEVER show for substitutions
    if (event.event_type === 'subst') return false;
    
    // ❌ NEVER show "Normal Goal"
    if (event.event_detail === 'Normal Goal') return false;
    
    // ✅ Show all other details (Penalty, Own Goal, etc.)
    return !!event.event_detail;
  };

  const HomeEvent = ({ event }: { event: typeof events[0] }) => {
    const isMissedPenalty = event.event_detail?.includes('Missed Penalty') || event.event_detail?.includes('Penalty Missed');
    
    return (
      <div className="flex items-center gap-2 py-3 px-3 border-b border-border">
        <Badge variant="secondary" className="flex-shrink-0 text-xs min-w-[42px] justify-center">
          {formatEventTime(event)}
        </Badge>
        <div className="flex-shrink-0">
          <EventIcon eventType={event.event_type} eventDetail={event.event_detail} />
        </div>
        {event.event_type === 'subst' ? (
          <div className="flex-1 min-w-0 space-y-0.5">
            <div className="flex items-center gap-1 text-sm">
              <ArrowUp className="w-3 h-3 text-green-500 flex-shrink-0" />
              <span className="text-green-500 font-medium truncate">{event.assist_name}</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <ArrowDown className="w-3 h-3 text-red-500 flex-shrink-0" />
              <span className="text-red-500 truncate">{event.player_name}</span>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-w-0 space-y-0">
            <div className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis flex items-center gap-1.5">
              <span>{event.player_name}</span>
              {event.player_id === -1 && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-4">
                  Coach
                </Badge>
              )}
              {isMissedPenalty && (
                <Badge variant="destructive" className="text-[10px] py-0 px-1.5 h-4">
                  Penalty miss
                </Badge>
              )}
            </div>
            {event.assist_name && !isMissedPenalty && (
              <div className="text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                ({event.assist_name})
              </div>
            )}
            {shouldShowEventDetail(event) && (
              <div className="text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                {event.event_detail}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const AwayEvent = ({ event }: { event: typeof events[0] }) => {
    const isMissedPenalty = event.event_detail?.includes('Missed Penalty') || event.event_detail?.includes('Penalty Missed');
    
    return (
      <div className="flex items-center gap-2 py-3 px-3 border-b border-border flex-row-reverse">
        <Badge variant="secondary" className="flex-shrink-0 text-xs min-w-[42px] justify-center">
          {formatEventTime(event)}
        </Badge>
        <div className="flex-shrink-0">
          <EventIcon eventType={event.event_type} eventDetail={event.event_detail} />
        </div>
        {event.event_type === 'subst' ? (
          <div className="flex-1 min-w-0 space-y-0.5">
            <div className="flex items-center justify-end gap-1 text-sm">
              <span className="text-green-500 font-medium truncate">{event.assist_name}</span>
              <ArrowUp className="w-3 h-3 text-green-500 flex-shrink-0" />
            </div>
            <div className="flex items-center justify-end gap-1 text-xs">
              <span className="text-red-500 truncate">{event.player_name}</span>
              <ArrowDown className="w-3 h-3 text-red-500 flex-shrink-0" />
            </div>
          </div>
        ) : (
          <div className="flex-1 min-w-0 space-y-0 text-right">
            <div className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis flex items-center justify-end gap-1.5">
              {isMissedPenalty && (
                <Badge variant="destructive" className="text-[10px] py-0 px-1.5 h-4">
                  Penalty miss
                </Badge>
              )}
              {event.player_id === -1 && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-4">
                  Coach
                </Badge>
              )}
              <span>{event.player_name}</span>
            </div>
            {event.assist_name && !isMissedPenalty && (
              <div className="text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                ({event.assist_name})
              </div>
            )}
            {shouldShowEventDetail(event) && (
              <div className="text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                {event.event_detail}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderEventsByMinute = (periodEvents: typeof events, startMinute: number, endMinute: number) => {
    // Create array of all events in chronological order
    const sortedEvents = [...periodEvents].sort((a, b) => {
      const timeA = a.time_elapsed + (a.time_extra || 0);
      const timeB = b.time_elapsed + (b.time_extra || 0);
      return timeA - timeB;
    });

    // Filter insight events for this period
    const periodInsights = insightEvents.filter(ie => 
      ie.time_elapsed >= startMinute && ie.time_elapsed <= endMinute
    );

    // Merge game events and insight events by minute
    type MergedItem = 
      | { kind: 'game'; event: typeof events[0] }
      | { kind: 'insight'; event: InsightEvent };

    const merged: MergedItem[] = [
      ...sortedEvents.map(e => ({ kind: 'game' as const, event: e })),
      ...periodInsights.map(e => ({ kind: 'insight' as const, event: e })),
    ].sort((a, b) => {
      const timeA = a.kind === 'game' 
        ? a.event.time_elapsed + ((a.event as any).time_extra || 0)
        : a.event.time_elapsed;
      const timeB = b.kind === 'game'
        ? b.event.time_elapsed + ((b.event as any).time_extra || 0)
        : b.event.time_elapsed;
      // Insight alerts before game events at same minute, confirms after
      if (timeA === timeB) {
        if (a.kind === 'insight' && (a.event as InsightEvent).type === 'insight-alert') return -1;
        if (b.kind === 'insight' && (b.event as InsightEvent).type === 'insight-alert') return 1;
        if (a.kind === 'insight' && (a.event as InsightEvent).type === 'insight-confirm') return 1;
        if (b.kind === 'insight' && (b.event as InsightEvent).type === 'insight-confirm') return -1;
      }
      return timeA - timeB;
    });
    
    return merged.map(item => {
      if (item.kind === 'insight') {
        const ie = item.event as InsightEvent;
        const isHome = ie.team_id === homeTeamId;
        const side = isHome ? 'home' : 'away';
        
        if (ie.type === 'insight-alert') {
          return <InsightAlertRow key={ie.id} event={ie} side={side} />;
        }
        // Dedicated var-disallowed event type (from renamed lock)
        if (ie.type === 'insight-var-disallowed') {
          return <InsightVarDisallowedRow key={ie.id} event={ie} side={side} varDetail="Goal Disallowed" />;
        }
        // Fallback: cross-reference VAR game events for older data
        if (ie.type === 'insight-confirm') {
          const varDisallowed = sortedEvents.find(e =>
            e.event_type === 'Var' &&
            e.team_id === ie.team_id &&
            e.event_detail?.includes('Goal Disallowed') &&
            Math.abs(e.time_elapsed - ie.time_elapsed) <= 3
          );
          if (varDisallowed) {
            return <InsightVarDisallowedRow key={ie.id} event={ie} side={side} varDetail={varDisallowed.event_detail} />;
          }
          return <InsightConfirmRow key={ie.id} event={ie} side={side} />;
        }
      }

      const event = item.event as typeof events[0];
      const isHomeEvent = event.team_id === homeTeamId;
      return isHomeEvent ? (
        <HomeEvent key={event.id} event={event} />
      ) : (
        <AwayEvent key={event.id} event={event} />
      );
    });
  };

  return (
    <div>
      {isLive && (
        <div className="flex justify-end mb-3">
          <Badge variant="outline" className="text-xs">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1 animate-pulse" />
            Live • {new Date(dataUpdatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </Badge>
        </div>
      )}
      <div className={cn(
        "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
        !inScrollableContainer && "max-h-[600px] overflow-y-auto"
      )}>
        {/* First Half */}
        {(periods.firstHalf.length > 0 || insightEvents.some(ie => ie.time_elapsed >= 1 && ie.time_elapsed <= 45)) && (
          <>
            <PeriodSeparator 
              label="FIRST HALF"
              score={gameStatus === 'finished' || gameStatus === 'live' ? undefined : undefined}
            />
            {renderEventsByMinute(periods.firstHalf, 1, 45)}
          </>
        )}

        {/* Second Half */}
        {(periods.secondHalf.length > 0 || insightEvents.some(ie => ie.time_elapsed >= 46 && ie.time_elapsed <= 90)) && (
          <>
            <PeriodSeparator 
              label="SECOND HALF"
              score={gameStatus === 'finished' && homeScore !== undefined && awayScore !== undefined 
                ? `${homeScore}-${awayScore}` 
                : undefined
              }
            />
            {renderEventsByMinute(periods.secondHalf, 46, 90)}
          </>
        )}

        {/* Extra Time */}
        {(periods.extraTime.length > 0 || insightEvents.some(ie => ie.time_elapsed >= 91 && ie.time_elapsed <= 120)) && (
          <>
            <PeriodSeparator label="EXTRA TIME" />
            {renderEventsByMinute(periods.extraTime, 91, 120)}
          </>
        )}

        {/* Final Score */}
        {gameStatus === 'finished' && homeScore !== undefined && awayScore !== undefined && (
          <div className="flex items-center justify-center gap-3 py-4 my-2 bg-accent/50 rounded-lg">
            <div className="flex-1 h-px bg-border" />
            <div className="flex items-center gap-2">
              {homeTeamLogo && (
                <Avatar className="h-7 w-7">
                  <AvatarImage src={homeTeamLogo} alt="Home" />
                  <AvatarFallback>H</AvatarFallback>
                </Avatar>
              )}
              <span className="text-lg font-bold">
                {homeScore}-{awayScore}
              </span>
              {awayTeamLogo && (
                <Avatar className="h-7 w-7">
                  <AvatarImage src={awayTeamLogo} alt="Away" />
                  <AvatarFallback>A</AvatarFallback>
                </Avatar>
              )}
            </div>
            <div className="flex-1 h-px bg-border" />
          </div>
        )}
      </div>
    </div>
  );
}
